import { css } from 'styled-components';
import { ISelectCssProps } from './Select';
import { useBaseFocusCss } from '../../fundamentals/actions';
import {
  useInputBorderCss,
  useInputFontSizeCss,
  useInputHeightCss,
  useInputWidthCss,
  useInputPaddingCss,
  useInputPaddingValue,
  useInputPlaceholderCss,
  useInputTextCss
} from '../TextField/textfieldStyles';
import { useCenteredFlexCss, useVerticalCenteredFlexCss } from '../../fundamentals/display';
import { inputIconSizes } from '../TextField/TextField';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { rgbaHexToRgbHex } from '../../fundamentals/colours/colourUtils';

export const selectColours = ({ base, text, palette }: IColoursCollection) => ({
  selectColours: {
    option: {
      text: text.default,
      hover: {
        background: palette.info25
      },
      selected: {
        background: palette.info,
        text: base.white.colour
      },
      disabled: {
        background: base.white.colour,
        text: palette.neutral200
      },
      active: {
        background: palette.info50
      }
    }
  }
});

export const SELECT_SPACE = 8;

export const useSelectCss = css<ISelectCssProps>`
  & {
    ${useCenteredFlexCss};
    .rselect__control {
      ${useInputWidthCss()};

      &,
      &:hover {
        ${useInputBorderCss};
      }

      min-height: 0;
      cursor: ${({ disabled }) => (disabled ? `inherit` : `pointer`)};
      ${useInputFontSizeCss};

      &.rselect__control--is-focused {
        ${useBaseFocusCss}
      }
      .rselect__value-container {
        min-height: ${({ theme, inputSize }) => {
          const borderSize = theme.textFieldColours.default.border.split(' ')[0];
          return `calc(${theme.sizes[inputSize!]} - ${borderSize} - ${borderSize})`;
        }};

        padding: 0 0 0 ${useInputPaddingValue};
      }

      .rselect__placeholder {
        margin-left: 0;
        margin-right: 0;
      }

      .rselect__input-container {
        padding-top: 0;
        padding-bottom: 0;
        margin: 0;
        ${useInputTextCss};
      }
    }

    .rselect__menu {
      ${useInputFontSizeCss};
    }

    .rselect__indicator-separator {
      display: none;
    }

    .rselect__indicator {
      padding: 0;
    }

    .rselect__dropdown-indicator {
      ${useInputPaddingCss};
    }

    .rselect__single-value {
      ${useInputTextCss};
    }

    .rselect__clear-indicator {
      padding-right: 0;
      height: ${({ theme, inputSize }) => theme.sizes[inputIconSizes[inputSize!]]};
      width: ${({ theme, inputSize }) => theme.sizes[inputIconSizes[inputSize!]]};
      &,
      * {
        ${useInputPlaceholderCss};
      }
    }
    .rselect__option {
      color: ${({ theme }) => theme.selectColours.option.text};
      ${useVerticalCenteredFlexCss};
      ${useInputHeightCss};
      ${useInputPaddingCss};

      &.rselect__option--is-selected {
        background-color: ${({ theme }) => theme.selectColours.option.selected.background};
        color: ${({ theme }) => theme.selectColours.option.selected.text};
      }

      &:not(.rselect__option--is-disabled) {
        cursor: pointer;
      }

      &.rselect__option--is-disabled {
        color: ${({ theme }) => theme.selectColours.option.disabled.text};
      }

      &:not(.rselect__option--is-disabled):not(.rselect__option--is-selected) {
        // equivalent to hovered
        &.rselect__option--is-focused {
          background-color: ${({ theme }) =>
            rgbaHexToRgbHex(
              theme.colours.eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.TABLE_ROW_COLOR_BACKGROUND_HOVER,
              theme.colours.eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND
            )};
        }
        &:active {
          background-color: ${({ theme }) => theme.colours.eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.TABLE_ROW_COLOR_BACKGROUND_SELECTED};
        }
      }
    }

    .rselect__control--menu-is-open {
      .rselect__dropdown-indicator.rotate-on-open {
        rotate: 180deg;
      }
    }

    .rselect__value-container.rselect__value-container--is-multi.rselect__value-container--has-value {
      column-gap: ${({ theme }) => theme.spaces[SELECT_SPACE]};

      padding-bottom: ${({ theme }) => theme.spaces[SELECT_SPACE]};

      .rselect__input-container {
        margin: 0;
        padding-top: ${({ theme }) => theme.spaces[SELECT_SPACE]};
        padding-bottom: 0;
        line-height: 1;
      }
    }
  }
`;
